/**
 * Rules for incentive line items on non-solar projects (Quote page).
 *
 * A quick-quote project uses `quote_mode === QUICK_QUOTE` (see `util/quoteMode.ts`).
 * On such projects every included incentive must satisfy:
 *   - Paid to installer (`incentive_paid_to === 1`)
 *   - Type is Fixed (`3`) or Price-Based (`2`) for library-backed rows
 *   - Type is Fixed (`3`) only for ad-hoc rows (inline trailing row is label + dollar amount)
 *
 * Any violation:
 *   - hides the row in the incentive library picker
 *   - blocks save in `useQuoteLineItemsSaveOnSubmit` (Layer 2)
 *   - is enforced server-side in `set_quote_line_items_on_project` (Layer 3, `QUOTE_SYSTEM_0005`)
 */

import {
  FIXED_INCENTIVE,
  INCENTIVE_PAID_TO_INSTALLER,
  PRICE_BASED_INCENTIVE,
} from 'resources/incentives/IncentiveConstants'

export { FIXED_INCENTIVE, INCENTIVE_PAID_TO_INSTALLER, PRICE_BASED_INCENTIVE }

export interface NonSolarIncentiveFieldErrors {
  incentive_type?: string
  incentive_paid_to?: string
}

export type NonSolarIncentiveRecord = {
  incentive_type: number | string
  incentive_paid_to?: number | string
  incentive_id?: number | null
}

/** Fields read by {@link isIncentiveCompatibleWithNsp}; optional for loose API/list records. */
export type NonSolarIncentiveLike = {
  incentive_type?: number | string | null
  incentive_paid_to?: number | string | null
  incentive_id?: number | null
  /** Incentive library list rows (picker); quote wire rows use `incentive_id` instead. */
  id?: number | null
}

const _NSP_ALLOWED_LIBRARY_TYPES = new Set([FIXED_INCENTIVE, PRICE_BASED_INCENTIVE])

function _hasIncentiveIdRef(incentiveId: number | null | undefined): boolean {
  return typeof incentiveId === 'number' && incentiveId > 0
}

/** Library-backed on the quote (`incentive_id`) or in the picker list (`id` only). */
function _isLibraryBackedIncentive(record: NonSolarIncentiveLike): boolean {
  if (_hasIncentiveIdRef(record.incentive_id)) {
    return true
  }
  const listId = record.id
  return typeof listId === 'number' && listId > 0
}

/**
 * Return `true` when an incentive record satisfies non-solar project rules.
 * Used for library filtering and save-time validation.
 */
export function isIncentiveCompatibleWithNsp(record: object): boolean {
  const { incentive_type, incentive_paid_to } = record as NonSolarIncentiveLike

  const paidTo =
    incentive_paid_to === null || incentive_paid_to === undefined
      ? INCENTIVE_PAID_TO_INSTALLER
      : Number(incentive_paid_to)

  if (paidTo !== INCENTIVE_PAID_TO_INSTALLER) {
    return false
  }

  const type = Number(incentive_type)
  if (!Number.isFinite(type)) {
    return false
  }

  if (_isLibraryBackedIncentive(record as NonSolarIncentiveLike)) {
    return _NSP_ALLOWED_LIBRARY_TYPES.has(type)
  }

  return type === FIXED_INCENTIVE
}

/**
 * Validate a single incentive row against non-solar project rules.
 *
 * @returns Field-level error strings, or `null` if fully valid.
 */
export function validateNonSolarIncentive(state: NonSolarIncentiveRecord): NonSolarIncentiveFieldErrors | null {
  if (isIncentiveCompatibleWithNsp(state)) {
    return null
  }

  const errors: NonSolarIncentiveFieldErrors = {}
  const paidTo =
    state.incentive_paid_to === null || state.incentive_paid_to === undefined
      ? INCENTIVE_PAID_TO_INSTALLER
      : Number(state.incentive_paid_to)

  if (paidTo !== INCENTIVE_PAID_TO_INSTALLER) {
    errors.incentive_paid_to = 'Incentive must be claimed by the installer for non-solar projects.'
  }

  const type = Number(state.incentive_type)
  if (_isLibraryBackedIncentive(state)) {
    if (!_NSP_ALLOWED_LIBRARY_TYPES.has(type)) {
      errors.incentive_type =
        "Incentive must be installer-claimed and either Fixed or Price-Based on non-solar projects."
    }
  } else if (type !== FIXED_INCENTIVE) {
    errors.incentive_type = "Ad-hoc incentives must use the Fixed type on non-solar projects."
  }

  return Object.keys(errors).length > 0 ? errors : null
}
